/**
 * Helper for rendering JSON-LD via TanStack head() scripts.
 * Use the buildXxx helpers to construct schema objects, and pass to head() via:
 *   scripts: [{ type: "application/ld+json", children: JSON.stringify(...) }]
 * This file exists for reuse from React components only when needed via SchemaJsonLd.
 */
import { SITE } from "@/lib/site";
import type { Faq } from "@/lib/faqs";

const SITE_URL = "https://mlcepoxy.ca";
const BUSINESS_ID = `${SITE_URL}/#business`;
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const abs = (path: string) => (path.startsWith("http") ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`);

function socialSameAs(): string[] {
  return [SITE.social.facebook, SITE.social.instagram, SITE.social.google].filter(
    (u): u is string =>
      typeof u === "string" &&
      /^https?:\/\/.+\/.+/.test(u) &&
      !/^https?:\/\/(www\.)?(instagram|facebook|google)\.com\/?$/.test(u)
  );
}
const LOGO_PATH = "/__l5e/assets-v1/52782658-57ff-475b-848b-84b88ee09b2a/mlc-logo.png";
const PRIMARY_IMAGE_PATH = "/__l5e/assets-v1/0f55760f-1f77-45cb-a090-aea282154802/project-flake-glossy.jpg";

const REPRESENTATIVE_IMAGES: { url: string; caption: string }[] = [
  { url: abs(PRIMARY_IMAGE_PATH), caption: "High-gloss flake epoxy garage floor — Calgary region install by MLC Epoxy Floors" },
  { url: abs("/__l5e/assets-v1/db6cefc6-e4d8-45a8-b03f-ea7b58128f4e/project-flake-blue.jpg"), caption: "Custom blue flake epoxy garage floor" },
  { url: abs("/__l5e/assets-v1/9502038f-a242-4f1d-ba98-9fd73d98c94b/project-flake-tan.jpg"), caption: "Tan flake epoxy garage floor with polyaspartic topcoat" },
  { url: abs("/__l5e/assets-v1/e2972ddd-940b-4595-9077-1608aab54796/project-garage-grey.jpg"), caption: "Grey solid color epoxy garage floor" },
  { url: abs("/__l5e/assets-v1/2cc1888e-75c9-4cf8-8302-2642b6310b84/project-broadcast.png"), caption: "Full-broadcast flake epoxy floor in progress" },
  { url: abs("/__l5e/assets-v1/2aefccc8-912e-41f0-a2ec-dbee1b227658/after-stair.jpg"), caption: "Epoxy-coated stair treads — completed install" },
];

export function buildOrganization() {
  const sameAs = socialSameAs();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: SITE.name,
    legalName: SITE.name,
    url: SITE_URL,
    logo: { "@type": "ImageObject", "@id": `${SITE_URL}/#logo`, url: abs(LOGO_PATH), contentUrl: abs(LOGO_PATH), caption: SITE.name },
    image: { "@id": `${SITE_URL}/#logo` },
    email: SITE.email,
    telephone: SITE.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postal,
      addressCountry: SITE.address.country,
    },
    contactPoint: [{
      "@type": "ContactPoint",
      telephone: SITE.phone,
      email: SITE.email,
      contactType: "customer service",
      areaServed: "CA-AB",
      availableLanguage: ["English"],
    }],
    ...(sameAs.length ? { sameAs } : {}),
  };
}

export function buildWebSite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_URL,
    name: SITE.name,
    inLanguage: "en-CA",
    publisher: { "@id": ORG_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/?q={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildLocalBusiness() {
  const sameAs = socialSameAs();
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": BUSINESS_ID,
    name: SITE.name,
    url: SITE_URL,
    image: REPRESENTATIVE_IMAGES.map((img) => ({
      "@type": "ImageObject",
      url: img.url,
      contentUrl: img.url,
      caption: img.caption,
    })),
    logo: { "@type": "ImageObject", "@id": `${SITE_URL}/#logo`, url: abs(LOGO_PATH), contentUrl: abs(LOGO_PATH), caption: SITE.name },
    photo: REPRESENTATIVE_IMAGES.map((img) => ({
      "@type": "Photograph",
      url: img.url,
      contentUrl: img.url,
      caption: img.caption,
    })),
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postal,
      addressCountry: SITE.address.country,
    },
    geo: { "@type": "GeoCoordinates", latitude: SITE.geo.lat, longitude: SITE.geo.lng },
    areaServed: [
      { "@type": "City", name: "Calgary" },
      { "@type": "City", name: "Airdrie" },
      { "@type": "City", name: "Chestermere" },
      { "@type": "City", name: "Cochrane" },
      { "@type": "City", name: "Okotoks" },
    ],
    openingHoursSpecification: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], opens: "00:00", closes: "23:59" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "127", bestRating: "5", worstRating: "1" },
    parentOrganization: { "@id": ORG_ID },
    isPartOf: { "@id": WEBSITE_ID },
    ...(sameAs.length ? { sameAs } : {}),
  };
}

export function buildCityLocalBusiness(city: {
  slug: string;
  name: string;
  state: string;
  region: string;
  landmarks: readonly string[];
}) {
  const sameAs = socialSameAs();
  const cityUrl = `${SITE_URL}/service-areas/${city.slug}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${city.name}, ${city.state}, Canada`)}`;
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": `${cityUrl}#localbusiness`,
    name: `${SITE.name} — ${city.name}`,
    alternateName: SITE.name,
    description: `Epoxy and polyaspartic floor coatings serving ${city.name}, ${city.state} and the surrounding ${city.region}.`,
    url: cityUrl,
    image: REPRESENTATIVE_IMAGES.map((img) => ({
      "@type": "ImageObject",
      url: img.url,
      contentUrl: img.url,
      caption: img.caption,
    })),
    logo: { "@type": "ImageObject", "@id": `${SITE_URL}/#logo`, url: abs(LOGO_PATH), contentUrl: abs(LOGO_PATH), caption: SITE.name },
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postal,
      addressCountry: SITE.address.country,
    },
    geo: { "@type": "GeoCoordinates", latitude: SITE.geo.lat, longitude: SITE.geo.lng },
    hasMap: mapsUrl,
    areaServed: [
      {
        "@type": "City",
        name: city.name,
        containedInPlace: { "@type": "AdministrativeArea", name: `${city.state}, Canada` },
      },
      ...city.landmarks.map((l) => ({
        "@type": "Place",
        name: `${l}, ${city.name}`,
      })),
    ],
    makesOffer: [
      "Flake Epoxy Flooring",
      "Metallic Epoxy Flooring",
      "Solid Color Epoxy Flooring",
      "Polyaspartic Floor Coatings",
      "Grind & Seal Concrete",
    ].map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: `${service} in ${city.name}`,
        areaServed: { "@type": "City", name: city.name },
        provider: { "@id": BUSINESS_ID },
      },
    })),
    openingHoursSpecification: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], opens: "00:00", closes: "23:59" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "127", bestRating: "5", worstRating: "1" },
    parentOrganization: { "@id": ORG_ID },
    branchOf: { "@id": BUSINESS_ID },
    isPartOf: { "@id": WEBSITE_ID },
    ...(sameAs.length ? { sameAs } : {}),
  };
}

const DEFAULT_AREA_SERVED = [
  { "@type": "City", name: "Calgary", containedInPlace: { "@type": "AdministrativeArea", name: "Alberta, Canada" } },
  { "@type": "City", name: "Airdrie", containedInPlace: { "@type": "AdministrativeArea", name: "Alberta, Canada" } },
  { "@type": "City", name: "Chestermere", containedInPlace: { "@type": "AdministrativeArea", name: "Alberta, Canada" } },
  { "@type": "City", name: "Cochrane", containedInPlace: { "@type": "AdministrativeArea", name: "Alberta, Canada" } },
  { "@type": "City", name: "Okotoks", containedInPlace: { "@type": "AdministrativeArea", name: "Alberta, Canada" } },
];

export function buildService(
  name: string,
  slug: string,
  description: string,
  opts?: { areaServed?: unknown; url?: string; serviceType?: string }
) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: opts?.serviceType ?? name,
    provider: { "@id": BUSINESS_ID },
    areaServed: opts?.areaServed ?? DEFAULT_AREA_SERVED,
    name,
    url: opts?.url ?? abs(`/services/${slug}`),
    description,
    isPartOf: { "@id": WEBSITE_ID },
  };
}

export function buildCityServices(city: { name: string; state: string; slug: string }, services: { name: string; slug: string; intro: string }[]) {
  const area = {
    "@type": "City",
    name: city.name,
    containedInPlace: { "@type": "AdministrativeArea", name: `${city.state}, Canada` },
  };
  const cityUrl = `/service-areas/${city.slug}`;
  return services.map((s) =>
    buildService(s.name, s.slug, `${s.intro} Serving ${city.name}, ${city.state}.`, {
      areaServed: area,
      url: abs(`${cityUrl}#${s.slug}`),
      serviceType: s.name,
    })
  );
}

export function buildFaqPage(faqs: Faq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function buildBreadcrumbs(items: { name: string; href: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.href),
    })),
  };
}

export function buildArticle(opts: { title: string; description: string; slug: string; datePublished: string }) {
  const url = abs(`/blog/${opts.slug}`);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.title,
    description: opts.description,
    image: [abs("/og-image.jpg")],
    author: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    isPartOf: { "@id": WEBSITE_ID },
    datePublished: opts.datePublished,
    dateModified: opts.datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };
}
